// Bookie · Jade — paper-invitation catalog card.
//
// A serene tea-ceremony preview. The outer frame is a tinted-ring (a gentle
// jade halo around the paper), with a hairline rectangle just inside.
// A plum-blossom medallion (single bloom on a curving twig) sits as the
// focal element. Eyebrow says TAKLIFNOMA, names in the template font, and a
// gold-tracked date+venue caption closes the card.
//
// All colors come from LayoutCtx so this layout adapts to whichever bookie
// template adopts it.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieJadePreviewLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(120% 80% at 50% 0%, ${accent}1c 0%, ${bg} 55%, ${accent}10 100%)`,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Plum-blossom medallion (photo or svg) */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            overflow: "hidden",
            border: `1px solid ${accent}88`,
            background: `radial-gradient(circle at 50% 50%, ${bg}, ${accent}10 100%)`,
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
                {/* Twig from lower-left to upper-right */}
                <path
                  d="M12 78 C 30 70 48 56 64 36 C 72 28 80 22 88 18"
                  stroke={accent}
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  fill="none"
                  opacity={0.85}
                />
                <path
                  d="M44 54 C 50 46 58 38 66 32"
                  stroke={accent}
                  strokeWidth="0.7"
                  strokeLinecap="round"
                  fill="none"
                  opacity={0.5}
                />
                {/* Primary 5-petal bloom */}
                <g transform="translate(68 28)">
                  {[0, 72, 144, 216, 288].map((deg, i) => (
                    <ellipse
                      key={i}
                      cx="0"
                      cy="-6"
                      rx="4"
                      ry="6"
                      transform={`rotate(${deg})`}
                      fill={ink}
                      opacity={0.18}
                    />
                  ))}
                  <circle r="1.6" fill={accent} />
                </g>
                {/* Secondary smaller bloom */}
                <g transform="translate(28 66)">
                  {[0, 72, 144, 216, 288].map((deg, i) => (
                    <ellipse
                      key={i}
                      cx="0"
                      cy="-4"
                      rx="2.5"
                      ry="4"
                      transform={`rotate(${deg})`}
                      fill={ink}
                      opacity={0.13}
                    />
                  ))}
                  <circle r="1.1" fill={accent} />
                </g>
              </svg>
            }
          />
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          aria-hidden
          style={{ width: 28, height: 1, background: accent, opacity: 0.7, margin: "3% 0 6px" }}
        />
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
